// Sorting/grouping helpers for the Camp Attendance sheet.
//
// Rows are ordered by program type (fixed order Robotics -> Coding -> Technology),
// then by course code (numeric-aware, e.g. SR150 before SR350), then by student name.
// Rows sharing the same course form a group; a blank separator row is rendered between groups.

export const PROGRAM_ORDER = ["robotics", "coding", "technology"];

export function programRank(programName) {
  const idx = PROGRAM_ORDER.indexOf((programName || "").trim().toLowerCase());
  // Unknown / missing programs are pushed to the end while keeping a stable order.
  return idx === -1 ? PROGRAM_ORDER.length : idx;
}

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

export function compareRows(a, b) {
  const rankDiff = programRank(a.program_name) - programRank(b.program_name);
  if (rankDiff !== 0) return rankDiff;

  // Fall back to program name for any programs outside the fixed order.
  const progNameDiff = collator.compare(a.program_name || "", b.program_name || "");
  if (progNameDiff !== 0) return progNameDiff;

  const codeDiff = collator.compare(a.course_code || "", b.course_code || "");
  if (codeDiff !== 0) return codeDiff;

  return collator.compare(a.student_name || "", b.student_name || "");
}

/**
 * Returns the rows in display order (program -> course code -> student name)
 * without any grouping metadata. Used for the on-screen table.
 */
export function sortRows(rows) {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort(compareRows);
}

/**
 * Returns the rows sorted in display order. Each row is tagged with
 * `__groupStart` = true when it begins a new course group (used to render the
 * blank separator row above it on screen / in the export).
 */
export function sortAndGroup(rows) {
  if (!Array.isArray(rows)) return [];

  const sorted = [...rows].sort(compareRows);

  let prevKey = null;
  return sorted.map((row) => {
    // Group on the course itself (program + course_code) so distinct courses are
    // always separated, even when abbreviations collide across programs.
    const key = `${row.program_type_id ?? ""}|${row.course_code ?? row.course_abbr ?? ""}`;
    const groupStart = prevKey !== null && prevKey !== key;
    prevKey = key;
    return { ...row, __groupStart: groupStart };
  });
}
